/**
 * Report Analytics Service
 * Tracks unique visits to saved report URLs for engagement analytics
 */

import { 
  collection, 
  doc, 
  setDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Interface for report visit data
 */
interface ReportVisit {
  reportId: string;
  organizationName: string;
  visitedAt: any; // Firestore timestamp
  visitorInfo: {
    userAgent: string;
    language: string;
    screenResolution: string;
    referrer: string;
    isFromEmail: boolean;
  };
  sessionId?: string;
  isUniqueVisit: boolean;
  visitorHash: string;
}

/**
 * Generate a hash for the visitor based on browser fingerprinting
 * This is non-PII and used only for uniqueness detection
 */
function generateVisitorHash(): string {
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.platform
  ].join('|');
  
  // Simple hash function (not cryptographic, just for uniqueness)
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Check if this visitor has already viewed this report
 * Note: This is a simplified check - in production, you might want to
 * implement server-side checking or use localStorage for better performance
 */
async function checkIfUniqueVisit(reportId: string, visitorHash: string): Promise<boolean> {
  try {
    // Check localStorage first for performance (client-side cache)
    const visitKey = `report_visit_${reportId}_${visitorHash}`;
    const hasVisited = localStorage.getItem(visitKey);
    
    if (hasVisited) {
      return false; // Not a unique visit
    }
    
    // Mark as visited in localStorage
    localStorage.setItem(visitKey, new Date().toISOString());
    return true; // This is a unique visit
  } catch (error) {
    console.error('Error checking unique visit:', error);
    // Default to logging the visit if we can't determine uniqueness
    return true;
  }
}

/**
 * Determine if the visitor came from an email link
 */
function isVisitFromEmail(): boolean {
  const referrer = document.referrer.toLowerCase();
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check for email tracking parameters or referrers
  return (
    (urlParams.has('utm_source') && urlParams.get('utm_source') === 'email') ||
    (urlParams.has('ref') && urlParams.get('ref') === 'email') ||
    referrer.includes('mail.google.com') ||
    referrer.includes('outlook.live.com') ||
    referrer.includes('mail.yahoo.com') ||
    referrer === '' // Direct link (often from email clients)
  );
}

/**
 * Log a visit to a report
 * @param reportId The unique report ID
 * @param organizationName The organization name from the report
 * @param sessionId Optional original session ID for reference
 */
export async function logReportVisit(
  reportId: string,
  organizationName: string,
  sessionId?: string
): Promise<void> {
  try {
    const visitorHash = generateVisitorHash();
    const isUnique = await checkIfUniqueVisit(reportId, visitorHash);
    
    // Only log if we haven't seen this visitor for this report before
    // or if it's been more than 24 hours (for returning visitors)
    const visitData: ReportVisit = {
      reportId,
      organizationName,
      visitedAt: serverTimestamp(),
      visitorInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        referrer: document.referrer || 'direct',
        isFromEmail: isVisitFromEmail()
      },
      sessionId: sessionId || '',
      isUniqueVisit: isUnique,
      visitorHash
    };
    
    // Generate a unique visit ID
    const visitId = doc(collection(db, 'temp')).id;
    
    // Save to Firestore
    const visitRef = doc(db, 'report_visits', visitId);
    await setDoc(visitRef, visitData);
  } catch (error) {
    // Don't throw errors for analytics - we don't want to break the app
    console.error('Failed to log report visit:', error);
  }
}

/**
 * Get visit analytics for a specific report (admin use)
 * Note: This requires appropriate Firestore rules for read access
 */
export async function getReportAnalytics(reportId: string): Promise<{
  totalVisits: number;
  uniqueVisitors: number;
  emailVisits: number;
  firstVisit?: Date;
  lastVisit?: Date;
}> {
  try {
    const visitsQuery = query(
      collection(db, 'report_visits'),
      where('reportId', '==', reportId)
    );
    
    const snapshot = await getDocs(visitsQuery);
    
    const uniqueVisitors = new Set<string>();
    let emailVisits = 0;
    let firstVisit: Date | undefined;
    let lastVisit: Date | undefined;
    
    snapshot.forEach(doc => {
      const data = doc.data() as ReportVisit;
      
      // Track unique visitors
      uniqueVisitors.add(data.visitorHash);
      
      // Count email visits
      if (data.visitorInfo.isFromEmail) {
        emailVisits++;
      }
      
      // Track first and last visit times
      if (data.visitedAt) {
        const visitDate = (data.visitedAt as Timestamp).toDate();
        if (!firstVisit || visitDate < firstVisit) {
          firstVisit = visitDate;
        }
        if (!lastVisit || visitDate > lastVisit) {
          lastVisit = visitDate;
        }
      }
    });
    
    return {
      totalVisits: snapshot.size,
      uniqueVisitors: uniqueVisitors.size,
      emailVisits,
      firstVisit,
      lastVisit
    };
  } catch (error) {
    console.error('Error fetching report analytics:', error);
    throw error;
  }
}

/**
 * Get organization engagement analytics (admin use)
 * Shows all visits for a specific organization
 */
export async function getOrganizationEngagement(organizationName: string): Promise<{
  totalReports: number;
  totalVisits: number;
  uniqueReports: Set<string>;
  engagementTimeline: Array<{ date: Date; visits: number }>;
}> {
  try {
    const visitsQuery = query(
      collection(db, 'report_visits'),
      where('organizationName', '==', organizationName)
    );
    
    const snapshot = await getDocs(visitsQuery);
    
    const uniqueReports = new Set<string>();
    const timeline = new Map<string, number>();
    
    snapshot.forEach(doc => {
      const data = doc.data() as ReportVisit;
      
      // Track unique reports
      uniqueReports.add(data.reportId);
      
      // Build engagement timeline
      if (data.visitedAt) {
        const visitDate = (data.visitedAt as Timestamp).toDate();
        const dateKey = visitDate.toISOString().split('T')[0]; // YYYY-MM-DD
        timeline.set(dateKey, (timeline.get(dateKey) || 0) + 1);
      }
    });
    
    // Convert timeline to array
    const engagementTimeline = Array.from(timeline.entries())
      .map(([date, visits]) => ({
        date: new Date(date),
        visits
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    
    return {
      totalReports: uniqueReports.size,
      totalVisits: snapshot.size,
      uniqueReports,
      engagementTimeline
    };
  } catch (error) {
    console.error('Error fetching organization engagement:', error);
    throw error;
  }
}

/**
 * Get visit counts for a specific report/session
 * @param reportId The report ID or session ID
 * @returns Visit count data
 */
export async function getReportVisitCounts(
  reportId: string
): Promise<{
  totalViews: number;
  uniqueViewers: number;
  lastViewedAt?: Date;
}> {
  try {
    const visitsQuery = query(
      collection(db, 'report_visits'),
      where('reportId', '==', reportId)
    );

    const visitsSnapshot = await getDocs(visitsQuery);
    const uniqueVisitors = new Set<string>();
    let lastViewedAt: Date | undefined;

    visitsSnapshot.forEach((doc) => {
      const visit = doc.data();
      if (visit.visitorHash) {
        uniqueVisitors.add(visit.visitorHash);
      }
      const visitTime = visit.visitedAt?.toDate ? visit.visitedAt.toDate() : new Date(visit.visitedAt);
      if (!lastViewedAt || visitTime > lastViewedAt) {
        lastViewedAt = visitTime;
      }
    });

    return {
      totalViews: visitsSnapshot.size,
      uniqueViewers: uniqueVisitors.size,
      lastViewedAt
    };
  } catch (error) {
    console.error('Error getting report visit counts:', error);
    return {
      totalViews: 0,
      uniqueViewers: 0
    };
  }
}